import { useState, type FC, type ReactNode } from "react";
import {
  ScrollView,
  useWindowDimensions,
  View,
  type LayoutChangeEvent,
  type StyleProp,
  type ViewStyle,
} from "react-native";
import { StyleSheet } from "react-native-unistyles";
import "../../theme";
import { TableLayoutProvider, TableScrollProvider } from "./TableContext";

export interface TableContainerProps {
  readonly children: ReactNode;
  /**
   * `"outlined"` (default) draws the bordered, rounded card surface.
   * `"plain"` only clips, for tables placed inside an existing card.
   */
  readonly variant?: "outlined" | "plain" | undefined;
  /**
   * Scroll sideways when the table's `minWidth` is wider than the container,
   * instead of clipping the right-hand columns.
   */
  readonly horizontalScroll?: boolean | undefined;
  /**
   * Container width, in pixels, below which the table switches to the stacked
   * layout: the header is hidden and each row's cells stack vertically. Read
   * the layout in cells with `useTableLayout`. Omit to always lay out columns.
   */
  readonly stackBelow?: number | undefined;
  /** Give it a bounded height (e.g. `flex: 1`) to scroll a long `TableBody` under a fixed header. */
  readonly style?: StyleProp<ViewStyle> | undefined;
  readonly testID?: string | undefined;
}

/** Surface that wraps a `Table`, like MUI's `TableContainer` with a `Paper`. */
const TableContainer: FC<TableContainerProps> = ({
  children,
  variant = "outlined",
  horizontalScroll = false,
  stackBelow,
  style,
  testID,
}) => {
  const window = useWindowDimensions();
  // Until the first layout, the window width is the best guess at the
  // container's, so a phone renders stacked from the first frame.
  const [measuredWidth, setMeasuredWidth] = useState<number | null>(null);
  const width = measuredWidth ?? window.width;
  const layout = stackBelow !== undefined && width < stackBelow ? "stacked" : "table";

  const onLayout =
    stackBelow === undefined ? undefined : (
      (event: LayoutChangeEvent) => {
        setMeasuredWidth(event.nativeEvent.layout.width);
      }
    );

  return (
    <View
      testID={testID}
      onLayout={onLayout}
      style={[styles.container, variant === "outlined" && styles.outlined, style]}
    >
      <TableLayoutProvider value={layout}>
        {/* Stacked rows fit the container's width, so there's nothing to scroll sideways. */}
        {horizontalScroll && layout === "table" ?
          <TableScrollProvider value>
            <ScrollView horizontal contentContainerStyle={styles.scrollContent}>
              {children}
            </ScrollView>
          </TableScrollProvider>
        : children}
      </TableLayoutProvider>
    </View>
  );
};

TableContainer.displayName = "TableContainer";

export { TableContainer };

const styles = StyleSheet.create((theme) => ({
  container: {
    width: "100%",
    overflow: "hidden",
  },
  outlined: {
    backgroundColor: theme.colors.surfaceCard,
    borderWidth: 1,
    borderColor: theme.colors.borderCard,
    borderRadius: theme.spacing.xl,
  },
  scrollContent: {
    // At least as wide as the container, so a table narrower than its
    // `minWidth` threshold still fills the card.
    flexGrow: 1,
  },
}));
